import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  FileDown,
  FileText,
  FolderOpen,
  HelpCircle,
  Pencil,
  Plus,
  Save,
  Search,
  Settings,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { faqApi } from '../../../api';
import useConfirm from '../../../hooks/useConfirm';
import { useUIStore } from '../../../store';

const tagFromValue = (value) => String(value || '').trim();

const getTagList = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    return tags
      .map((tag) => (typeof tag === 'string' ? tag : tag?.name || tag?.tag_name))
      .map(tagFromValue)
      .filter(Boolean);
  }
  if (typeof tags === 'string') {
    return tags
      .split(/[,|_]/)
      .map(tagFromValue)
      .filter(Boolean);
  }
  return [];
};

const mapFaq = (faq) => ({
  id: faq?.faq_id ?? faq?.id,
  question: faq?.faq_question ?? faq?.question ?? '',
  answer: faq?.faq_answer ?? faq?.answer ?? '',
  categoryId: faq?.cid ?? faq?.category_id ?? faq?.categoryId ?? '',
  categoryName: faq?.category_name ?? faq?.categoryName ?? faq?.category ?? '',
  tags: getTagList(faq?.tags),
  status: faq?.faq_status ?? faq?.status ?? 0,
});

const mapCategory = (category) => ({
  id: category?.id ?? category?.cid ?? category?.category_id,
  name: category?.name ?? category?.c_name ?? '',
  status: category?.status ?? category?.c_status ?? 1,
});

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color = 'primary.main', bgColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: bgColor || alpha(color, 0.04),
      flex: 1,
      minWidth: 140,
    }}
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: alpha(color, 0.1),
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={22} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const FaqPage = ({ session = {} }) => {
  const { confirm } = useConfirm();
  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCategoryList, setShowCategoryList] = useState(false);

  const [addCategoryId, setAddCategoryId] = useState('');
  const [addQuestion, setAddQuestion] = useState('');
  const [addAnswer, setAddAnswer] = useState('');
  const [addTags, setAddTags] = useState([]);
  const [addTagInput, setAddTagInput] = useState('');

  const [editId, setEditId] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState('');

  const [categoryForm, setCategoryForm] = useState({ id: null, name: '', status: 1 });
  const [categoryError, setCategoryError] = useState('');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const isAdmin = ['admin', 'superadmin'].includes(
    String(session?.role || session?.u_role || '').toLowerCase()
  );

  useEffect(() => {
    fetchFaqs();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!successMsg) return undefined;
    showSnackbar({ message: successMsg, severity: 'success' });
    const timer = setTimeout(() => setSuccessMsg(''), 3000);
    return () => clearTimeout(timer);
  }, [showSnackbar, successMsg]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFaqs(searchTerm.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchFaqs = async (query = '') => {
    setLoading(true);
    setErrorMsg('');
    try {
      const response = query ? await faqApi.search(query) : await faqApi.getAll();
      const result = response?.data?.data || response?.data || [];
      const mapped = Array.isArray(result) ? result.map(mapFaq) : [];
      setFaqs(mapped);
      setSelectedIds(new Set());
      setCurrentPage(1);
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to load FAQs');
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await faqApi.getCategories();
      const result = response?.data?.data || response?.data || [];
      setCategories(Array.isArray(result) ? result.map(mapCategory) : []);
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to load categories');
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const resetAddForm = () => {
    setAddCategoryId('');
    setAddQuestion('');
    setAddAnswer('');
    setAddTags([]);
    setAddTagInput('');
  };

  const resetEditForm = () => {
    setEditId('');
    setEditCategoryId('');
    setEditQuestion('');
    setEditAnswer('');
    setEditTags([]);
    setEditTagInput('');
  };

  const handleAddTag = () => {
    const value = tagFromValue(addTagInput);
    if (!value || addTags.includes(value)) return;
    setAddTags([...addTags, value]);
    setAddTagInput('');
  };

  const handleEditTagAdd = () => {
    const value = tagFromValue(editTagInput);
    if (!value || editTags.includes(value)) return;
    setEditTags([...editTags, value]);
    setEditTagInput('');
  };

  const handleAddFAQ = async (e) => {
    e.preventDefault();
    if (!addCategoryId || !addQuestion.trim() || !addAnswer.trim()) {
      setErrorMsg('Please fill all required fields.');
      return;
    }
    setErrorMsg('');
    try {
      await faqApi.create({
        categoryId: addCategoryId,
        question: addQuestion.trim(),
        answer: addAnswer.trim(),
        tags: addTags,
      });
      setSuccessMsg('FAQ created successfully.');
      resetAddForm();
      setShowAddModal(false);
      fetchFaqs(searchTerm.trim());
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to create FAQ');
    }
  };

  const handleEditFAQ = async (e) => {
    e.preventDefault();
    if (!editId || !editCategoryId || !editQuestion.trim() || !editAnswer.trim()) {
      setErrorMsg('Please fill all required fields.');
      return;
    }
    setErrorMsg('');
    try {
      await faqApi.update(editId, {
        categoryId: editCategoryId,
        question: editQuestion.trim(),
        answer: editAnswer.trim(),
        tags: editTags,
      });
      setSuccessMsg('FAQ updated successfully.');
      resetEditForm();
      setShowEditModal(false);
      fetchFaqs(searchTerm.trim());
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to update FAQ');
    }
  };

  const handleEdit = (faq) => {
    setEditId(faq.id);
    setEditCategoryId(faq.categoryId || '');
    setEditQuestion(faq.question || '');
    setEditAnswer(faq.answer || '');
    setEditTags(faq.tags || []);
    setEditTagInput('');
    setShowEditModal(true);
  };

  const handleDelete = async (faq) => {
    const isConfirmed = await confirm({
      title: 'Delete FAQ',
      message: 'Are you sure you want to delete this FAQ?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;
    try {
      await faqApi.delete(faq.id);
      setSuccessMsg('FAQ deleted successfully.');
      fetchFaqs(searchTerm.trim());
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to delete FAQ');
    }
  };

  const handleToggleStatus = async (faq) => {
    try {
      const response = await faqApi.toggleStatus(faq.id);
      const result = response?.data?.data || response?.data || faq;
      const updated = mapFaq({
        ...result,
        category_id: result?.category_id ?? faq?.categoryId,
        category_name: result?.category_name ?? faq?.categoryName,
        tags: result?.tags ?? faq?.tags,
      });
      setFaqs((prev) => prev.map((item) => (item.id === faq.id ? updated : item)));
      setSuccessMsg('FAQ status updated.');
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to update status');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) {
      setErrorMsg('Select at least one FAQ to delete.');
      return;
    }
    const isConfirmed = await confirm({
      title: 'Delete FAQs',
      message: 'Delete selected FAQs?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;
    try {
      await faqApi.bulkDelete(Array.from(selectedIds));
      setSuccessMsg('FAQs deleted successfully.');
      fetchFaqs(searchTerm.trim());
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to delete FAQs');
    }
  };

  const moveFaq = (index, direction) => {
    setFaqs((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const order = faqs.map((faq) => faq.id).filter(Boolean);
      await faqApi.reorder({ faqIds: order });
      setSuccessMsg('FAQ order saved.');
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleSelectOne = (faqId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(faqId);
      } else {
        next.delete(faqId);
      }
      return next;
    });
  };

  const handleSelectAll = (checked, pageFaqs) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        pageFaqs.forEach((faq) => next.add(faq.id));
      } else {
        pageFaqs.forEach((faq) => next.delete(faq.id));
      }
      return next;
    });
  };

  const handleExport = async () => {
    try {
      const response = await faqApi.export(exportFormat);
      const contentType = response?.headers?.['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFormat === 'pdf' ? 'faqs.pdf' : 'faqs.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to export FAQs');
    }
  };

  const handleImportClick = () => {
    if (importInputRef.current) {
      importInputRef.current.value = '';
      importInputRef.current.click();
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    try {
      const response = await faqApi.import(formData);
      const result = response?.data?.data || response?.data || {};
      setSuccessMsg(`Import complete. Added ${result.success || 0}, failed ${result.failed || 0}.`);
      fetchFaqs(searchTerm.trim());
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to import FAQs');
    } finally {
      setImporting(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    const name = String(categoryForm.name || '').trim();
    if (!name) {
      setCategoryError('Category name is required.');
      return;
    }
    setCategoryError('');
    try {
      if (categoryForm.id) {
        await faqApi.updateCategory(categoryForm.id, {
          name,
          status: categoryForm.status,
        });
        setSuccessMsg('Category updated.');
      } else {
        await faqApi.createCategory({ name, status: categoryForm.status });
        setSuccessMsg('Category created.');
      }
      setCategoryForm({ id: null, name: '', status: 1 });
      fetchCategories();
    } catch (error) {
      setCategoryError(error?.response?.data?.error || 'Failed to save category');
    }
  };

  const handleCategoryEdit = (category) => {
    setCategoryForm({
      id: category.id,
      name: category.name,
      status: category.status ?? 1,
    });
    setCategoryError('');
  };

  const handleCategoryDelete = async (categoryId) => {
    const isConfirmed = await confirm({
      title: 'Delete Category',
      message: 'Delete this category?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;
    try {
      await faqApi.deleteCategory(categoryId);
      setSuccessMsg('Category deleted.');
      fetchCategories();
    } catch (error) {
      setCategoryError(error?.response?.data?.error || 'Failed to delete category');
    }
  };

  const totalPages = Math.ceil(faqs.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = faqs.slice(startIndex, startIndex + rowsPerPage);
  const allSelectedOnPage = currentData.length > 0 && currentData.every((faq) => selectedIds.has(faq.id));
  const canReorder = !searchTerm.trim();

  // Stats calculations
  const totalFaqs = faqs.length;
  const activeFaqs = faqs.filter((f) => f.status === 1).length;
  const inactiveFaqs = faqs.filter((f) => f.status === 0).length;
  const totalCategories = categories.length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              FAQ Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage frequently asked questions and categories
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<FileDown size={18} />}
              component="a"
              href="/assets/BulkUploadFaqFormat.xlsx"
              download
              sx={{ borderRadius: 2 }}
            >
              Sample Format
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => setShowAddModal(true)}
              sx={{ borderRadius: 2 }}
            >
              Add FAQ
            </Button>
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={<Settings size={18} />}
                onClick={() => setShowCategoryModal(true)}
                sx={{ borderRadius: 2 }}
              >
                Categories
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
        flexWrap="wrap"
      >
        <StatCard
          icon={HelpCircle}
          label="Total FAQs"
          value={totalFaqs}
          color="#6366f1"
        />
        <StatCard
          icon={CheckCircle}
          label="Active"
          value={activeFaqs}
          color="#22c55e"
        />
        <StatCard
          icon={XCircle}
          label="Inactive"
          value={inactiveFaqs}
          color="#f59e0b"
        />
        <StatCard
          icon={FolderOpen}
          label="Categories"
          value={totalCategories}
          color="#3b82f6"
        />
      </Stack>

      {/* Alerts */}
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {/* Toolbar */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            {/* Search */}
            <TextField
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color="#9ca3af" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Actions */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {selectedIds.size > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Trash2 size={16} />}
                  onClick={handleBulkDelete}
                  sx={{ borderRadius: 2 }}
                >
                  Delete ({selectedIds.size})
                </Button>
              )}

              <Button
                variant="outlined"
                size="small"
                startIcon={<Upload size={16} />}
                onClick={handleImportClick}
                disabled={importing}
                sx={{ borderRadius: 2 }}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <Select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="xlsx">XLSX</MenuItem>
                    <MenuItem value="pdf">PDF</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download size={16} />}
                  onClick={handleExport}
                  sx={{ borderRadius: 2 }}
                >
                  Export
                </Button>
              </Stack>

              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpen size={16} />}
                onClick={() => setShowCategoryList(true)}
                sx={{ borderRadius: 2 }}
              >
                View Categories
              </Button>

              {canReorder && faqs.length >= 2 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Save size={16} />}
                  onClick={handleSaveOrder}
                  disabled={savingOrder}
                  sx={{ borderRadius: 2 }}
                >
                  {savingOrder ? 'Saving...' : 'Save Order'}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell padding="checkbox" sx={{ width: 50 }}>
                  <Checkbox
                    checked={allSelectedOnPage}
                    indeterminate={!allSelectedOnPage && selectedIds.size > 0}
                    onChange={(e) => handleSelectAll(e.target.checked, currentData)}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 60 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Question</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 250 }}>Answer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 140 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading FAQs...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 8 }}>
                    <Stack alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: '50%',
                          bgcolor: 'grey.100',
                        }}
                      >
                        <FileText size={40} color="#9ca3af" />
                      </Box>
                      <Typography color="text.secondary">
                        {searchTerm ? 'No FAQs match your search' : 'No FAQs available'}
                      </Typography>
                      {!searchTerm && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Plus size={16} />}
                          onClick={() => setShowAddModal(true)}
                        >
                          Add Your First FAQ
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                currentData.map((item, index) => {
                  const globalIndex = startIndex + index;
                  return (
                    <TableRow
                      key={item.id || index}
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        bgcolor: selectedIds.has(item.id) ? alpha('#6366f1', 0.04) : 'transparent',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onChange={(e) => handleSelectOne(item.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {globalIndex + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.categoryName || 'Uncategorized'}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {item.question || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {item.answer || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                          {item.tags.length ? (
                            <>
                              {item.tags.slice(0, 2).map((tag) => (
                                <Chip
                                  key={`${item.id}-${tag}`}
                                  label={tag}
                                  size="small"
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor: alpha('#6366f1', 0.1),
                                    color: '#6366f1',
                                    fontSize: '0.75rem',
                                  }}
                                />
                              ))}
                              {item.tags.length > 2 && (
                                <Chip
                                  label={`+${item.tags.length - 2}`}
                                  size="small"
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor: 'grey.100',
                                    fontSize: '0.75rem',
                                  }}
                                />
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={item.status === 1}
                          onChange={() => handleToggleStatus(item)}
                          color="success"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {canReorder && (
                            <>
                              <Tooltip title="Move up">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveFaq(globalIndex, -1)}
                                    disabled={globalIndex === 0}
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <ArrowUp size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveFaq(globalIndex, 1)}
                                    disabled={globalIndex === faqs.length - 1}
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <ArrowDown size={16} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item)}
                              sx={{ color: 'primary.main' }}
                            >
                              <Pencil size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(item)}
                              sx={{ color: 'error.main' }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                Showing {startIndex + 1} - {Math.min(startIndex + rowsPerPage, faqs.length)} of {faqs.length}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={20} />
                </IconButton>
                <Stack direction="row" spacing={0.5}>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        variant={currentPage === pageNum ? 'contained' : 'text'}
                        onClick={() => setCurrentPage(pageNum)}
                        sx={{
                          minWidth: 36,
                          borderRadius: 1.5,
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={20} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        )}
      </Card>

      {/* Hidden file input */}
      <input
        ref={importInputRef}
        type="file"
        hidden
        accept=".xlsx,.xls,.csv"
        onChange={handleImport}
      />

      {/* Add FAQ Dialog */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Add New FAQ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a new frequently asked question
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <FormControl fullWidth required size="small">
              <InputLabel id="add-category-label">Category</InputLabel>
              <Select
                labelId="add-category-label"
                label="Category"
                value={addCategoryId}
                onChange={(e) => setAddCategoryId(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">
                  <em>{loadingCategories ? 'Loading...' : '-- Select Category --'}</em>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Question"
              value={addQuestion}
              onChange={(e) => setAddQuestion(e.target.value)}
              required
              fullWidth
              size="small"
              placeholder="Enter the question..."
              InputProps={{ sx: { borderRadius: 2 } }}
            />
            <TextField
              label="Answer"
              value={addAnswer}
              onChange={(e) => setAddAnswer(e.target.value)}
              required
              multiline
              minRows={3}
              fullWidth
              size="small"
              placeholder="Enter the answer..."
              InputProps={{ sx: { borderRadius: 2 } }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Tags
              </Typography>
              <TextField
                placeholder="Type a tag and press Enter"
                value={addTagInput}
                onChange={(e) => setAddTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                fullWidth
                size="small"
                InputProps={{ sx: { borderRadius: 2 } }}
              />
              {addTags.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1.5, gap: 0.5 }}>
                  {addTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => setAddTags(addTags.filter((item) => item !== tag))}
                      sx={{ borderRadius: 1.5 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setShowAddModal(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddFAQ}
            sx={{ borderRadius: 2 }}
          >
            Create FAQ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit FAQ Dialog */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Edit FAQ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update the FAQ details
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <FormControl fullWidth required size="small">
              <InputLabel id="edit-category-label">Category</InputLabel>
              <Select
                labelId="edit-category-label"
                label="Category"
                value={editCategoryId}
                onChange={(e) => setEditCategoryId(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">
                  <em>{loadingCategories ? 'Loading...' : '-- Select Category --'}</em>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Question"
              value={editQuestion}
              onChange={(e) => setEditQuestion(e.target.value)}
              required
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 } }}
            />
            <TextField
              label="Answer"
              value={editAnswer}
              onChange={(e) => setEditAnswer(e.target.value)}
              required
              multiline
              minRows={3}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 } }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Tags
              </Typography>
              <TextField
                placeholder="Type a tag and press Enter"
                value={editTagInput}
                onChange={(e) => setEditTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleEditTagAdd();
                  }
                }}
                fullWidth
                size="small"
                InputProps={{ sx: { borderRadius: 2 } }}
              />
              {editTags.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1.5, gap: 0.5 }}>
                  {editTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => setEditTags(editTags.filter((item) => item !== tag))}
                      sx={{ borderRadius: 1.5 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setShowEditModal(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditFAQ}
            sx={{ borderRadius: 2 }}
          >
            Update FAQ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog
        open={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Manage Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage FAQ categories
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {categoryError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {categoryError}
              </Alert>
            )}
            <Box component="form" onSubmit={handleCategorySubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Category Name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  fullWidth
                  size="small"
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel id="category-status-label">Status</InputLabel>
                  <Select
                    labelId="category-status-label"
                    label="Status"
                    value={categoryForm.status}
                    onChange={(e) =>
                      setCategoryForm((prev) => ({ ...prev, status: Number(e.target.value) }))
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => setCategoryForm({ id: null, name: '', status: 1 })}
                    sx={{ borderRadius: 2 }}
                  >
                    Clear
                  </Button>
                  <Button type="submit" variant="contained" sx={{ borderRadius: 2 }}>
                    {categoryForm.id ? 'Update' : 'Create'}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Existing Categories
              </Typography>
              {categories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                  No categories available.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {categories.map((category) => (
                    <Paper
                      key={category.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography fontWeight={500}>{category.name}</Typography>
                        <Chip
                          label={category.status === 1 ? 'Active' : 'Inactive'}
                          size="small"
                          color={category.status === 1 ? 'success' : 'default'}
                          sx={{ width: 'fit-content', borderRadius: 1 }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleCategoryEdit(category)}
                          sx={{ borderRadius: 1.5 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleCategoryDelete(category.id)}
                          sx={{ borderRadius: 1.5 }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setShowCategoryModal(false)}
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category List Dialog */}
      <Dialog
        open={showCategoryList}
        onClose={() => setShowCategoryList(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all FAQ categories
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              No categories available.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {categories.map((category) => (
                <Paper
                  key={category.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: alpha('#6366f1', 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FolderOpen size={20} color="#6366f1" />
                    </Box>
                    <Stack spacing={0.25}>
                      <Typography fontWeight={500}>{category.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {category.id}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Chip
                    label={category.status === 1 ? 'Active' : 'Inactive'}
                    size="small"
                    color={category.status === 1 ? 'success' : 'default'}
                    sx={{ borderRadius: 1 }}
                  />
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setShowCategoryList(false)}
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FaqPage;
